import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
  Textarea,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  createVotingPeriod,
  CreateVotingPeriodRequest,
  Criterion,
} from "utils/apiService/PinkSale";

interface CreateVotingPeriodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface CreateVotingPeriodFormData {
  time: string;
  eligibilityCriteria: string;
}

// Default eligibility criteria as formatted JSON string
const DEFAULT_ELIGIBILITY_CRITERIA = JSON.stringify(
  [{ type: "minRaised", enabled: true, params: { amountUsd: 1000 } }],
  null,
  2
);

export default function CreateVotingPeriodModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateVotingPeriodModalProps) {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateVotingPeriodFormData>({
    defaultValues: {
      time: "",
      eligibilityCriteria: DEFAULT_ELIGIBILITY_CRITERIA,
    },
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      reset({
        time: "",
        eligibilityCriteria: DEFAULT_ELIGIBILITY_CRITERIA,
      });
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: CreateVotingPeriodFormData) => {
    try {
      // Convert datetime-local format to ISO string
      const formatToISO = (dateTimeLocal: string) => {
        if (!dateTimeLocal) return "";
        return new Date(dateTimeLocal).toISOString();
      };

      // Parse eligibilityCriteria JSON
      let eligibilityCriteria: Criterion[] = [];
      if (data.eligibilityCriteria.trim()) {
        try {
          eligibilityCriteria = JSON.parse(data.eligibilityCriteria);
          // Validate that it's an array
          if (!Array.isArray(eligibilityCriteria)) {
            throw new Error("Eligibility criteria must be an array");
          }
        } catch (parseError: any) {
          toast({
            title: "Invalid JSON",
            description:
              parseError?.message ||
              "Eligibility criteria must be valid JSON array",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      const payload: CreateVotingPeriodRequest = {
        time: formatToISO(data.time),
        eligibilityCriteria:
          eligibilityCriteria.length > 0 ? eligibilityCriteria : undefined,
      };

      await createVotingPeriod(payload);

      toast({
        title: "Success",
        description: "Voting period created successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to create voting period",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    reset({
      time: "",
      eligibilityCriteria: DEFAULT_ELIGIBILITY_CRITERIA,
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Voting Period</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={Boolean(errors.time)} isRequired>
                <FormLabel>Time</FormLabel>
                <Input
                  type="datetime-local"
                  {...register("time", {
                    required: "Time is required",
                    validate: (value) => {
                      if (!value) return "Time is required";
                      const selectedDate = new Date(value);
                      if (isNaN(selectedDate.getTime())) {
                        return "Invalid date format";
                      }
                      return true;
                    },
                  })}
                />
                <FormErrorMessage>{errors.time?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={Boolean(errors.eligibilityCriteria)}>
                <FormLabel>Eligibility Criteria (JSON Array)</FormLabel>
                <Textarea
                  placeholder='[{"type": "minRaised", "enabled": true, "params": {"amountUsd": 1000}}]'
                  rows={8}
                  fontFamily="mono"
                  fontSize="sm"
                  {...register("eligibilityCriteria", {
                    validate: (value) => {
                      // If empty, it's valid (optional field)
                      if (!value || !value.trim()) {
                        return true;
                      }
                      // Try to parse JSON
                      try {
                        const parsed = JSON.parse(value);
                        // Validate that it's an array
                        if (!Array.isArray(parsed)) {
                          return "Eligibility criteria must be a JSON array of objects";
                        }
                        // Validate array items structure - each must be a Criterion object
                        for (const item of parsed) {
                          if (typeof item !== "object" || item === null) {
                            return "Each criterion must be an object";
                          }
                          if (typeof item.type !== "string") {
                            return "Each criterion must have a 'type' string property";
                          }
                          if (typeof item.enabled !== "boolean") {
                            return "Each criterion must have an 'enabled' boolean property";
                          }
                          if (!item.params || typeof item.params !== "object") {
                            return "Each criterion must have a 'params' object property";
                          }
                        }
                        return true;
                      } catch (parseError) {
                        return "Invalid JSON format";
                      }
                    },
                  })}
                />
                <FormErrorMessage>
                  {errors.eligibilityCriteria?.message}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              type="submit"
              isLoading={isSubmitting}
            >
              Create
            </Button>
            <Button onClick={handleClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
